<!-- PROVISÓRIO (R2-X-4 = B, 7-3): the report period until the kit has presets.
     Built on the plant of the kit's DateRangeFilter (src/product/components/DateRangeFilter.vue: an outline `sm` button
     that says the period, a popover with the registry range calendar, one line of help and Apply; a report period is
     `required`, so no Clear) plus what the woot's DatePicker has and the kit does not: the eight presets of
     `DatePickerHelper` on the side, and the previous/next arrows of the periods that move (this week, this month).
     Same contract as the woot's DatePicker (`v-model:date-range`, `v-model:range-type`, `dateRangeChanged`), so the
     report filters keep their URL and payloads. Dates are said in the user's locale, not `en-US`.
     `@internationalized/date` is not reachable from the app without a `pnpm install` (it only comes inside reka-ui), so
     the calendar values are made and read with `reka-ui/date`. Swap for the kit's filter when it has presets. -->
<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { parseStringToDateValue, toDate } from 'reka-ui/date';
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  endOfDay,
  format,
  getWeek,
  startOfDay,
  subDays,
} from 'date-fns';
import {
  Calendar16,
  ChevronDown16,
  ChevronLeft16,
  ChevronRight16,
} from '@carbon/icons-vue';
import {
  dateRanges,
  getActiveDateRange,
  getRangeAtOffset,
  isNavigableRange,
  DATE_RANGE_TYPES,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { RangeCalendar } from '../ui/range-calendar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const props = defineProps({
  align: { type: String, default: 'start' },
});
const emit = defineEmits(['dateRangeChanged']);
const dateRange = defineModel('dateRange', { type: Array, default: undefined });
const rangeType = defineModel('rangeType', { type: String, default: '' });

const { t, locale } = useI18n();
const WEEK_STARTS_ON = 1; // Monday, as the woot's picker
const GREGORIAN = { calendar: { identifier: 'gregory' } };
const toValue = date =>
  parseStringToDateValue(format(date, 'yyyy-MM-dd'), GREGORIAN);
const intlLocale = computed(() => String(locale.value).replace('_', '-'));

const range = computed(() => {
  const [start, end] = dateRange.value || [];
  if (start && end) return { start, end };
  return {
    start: startOfDay(subDays(new Date(), 6)),
    end: endOfDay(new Date()),
  };
});
const type = computed(() => rangeType.value || DATE_RANGE_TYPES.LAST_7_DAYS);

const choosing = ref(false);
const picked = shallowRef({ start: undefined, end: undefined });
const twoMonths = useMediaQuery('(min-width: 1024px)');
watch(choosing, open => {
  if (open)
    picked.value = {
      start: toValue(range.value.start),
      end: toValue(range.value.end),
    };
});

const commit = (start, end, kind) => {
  dateRange.value = [start, end];
  rangeType.value = kind;
  emit('dateRangeChanged', [start, end, kind]);
};
const choosePreset = kind => {
  if (kind === DATE_RANGE_TYPES.CUSTOM_RANGE) return;
  const { start, end } = getActiveDateRange(kind, new Date());
  commit(start, end, kind);
  choosing.value = false;
};
const apply = () => {
  commit(
    startOfDay(toDate(picked.value.start)),
    endOfDay(toDate(picked.value.end)),
    DATE_RANGE_TYPES.CUSTOM_RANGE
  );
  choosing.value = false;
};

// This week and this month move by whole periods; the current one is the latest.
const navigable = computed(() => isNavigableRange(type.value));
const currentPeriod = computed(() =>
  getActiveDateRange(type.value, new Date())
);
const offset = computed(() =>
  type.value === DATE_RANGE_TYPES.THIS_WEEK
    ? differenceInCalendarWeeks(range.value.start, currentPeriod.value.start, {
        weekStartsOn: WEEK_STARTS_ON,
      })
    : differenceInCalendarMonths(range.value.start, currentPeriod.value.start)
);
const canGoNext = computed(
  () => navigable.value && range.value.start < currentPeriod.value.start
);
const move = step => {
  const next = Math.min(offset.value + step, 0);
  const { start, end } = getRangeAtOffset(type.value, next, new Date());
  commit(start, end, type.value);
};

const presetLabel = computed(() => {
  if (type.value === DATE_RANGE_TYPES.MONTH_TO_DATE && offset.value < 0)
    return new Intl.DateTimeFormat(intlLocale.value, { month: 'long' }).format(
      range.value.start
    );
  if (type.value === DATE_RANGE_TYPES.THIS_WEEK && offset.value < 0)
    return t('DATE_PICKER.WEEK_NUMBER', {
      weekNumber: getWeek(range.value.start, { weekStartsOn: WEEK_STARTS_ON }),
    });
  const preset = dateRanges.find(item => item.value === type.value);
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return preset ? t(preset.label) : '';
});
const words = computed(() => {
  const { start, end } = range.value;
  const say = new Intl.DateTimeFormat(intlLocale.value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return differenceInCalendarDays(end, start) === 0
    ? say.format(start)
    : say.formatRange(start, end);
});
</script>

<template>
  <div class="flex min-w-0 items-center gap-1" data-report-period>
    <Popover v-model:open="choosing">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          size="sm"
          class="min-w-0 max-w-full"
          data-date-range
          :data-range-type="type"
        >
          <Calendar16 aria-hidden="true" />
          <span class="sr-only">{{ t('POLARIS_REPORTS.PERIOD.LABEL') }}:</span>
          <span class="truncate">{{ presetLabel }}</span>
          <span class="truncate font-normal text-polaris-muted-foreground">
            {{ words }}
          </span>
          <ChevronDown16 aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        :align="props.align"
        :collision-padding="16"
        class="w-auto max-w-[calc(100vw-2rem)] p-0"
      >
        <div class="flex flex-col sm:flex-row">
          <div
            class="flex flex-col gap-0.5 border-0 border-b border-solid border-polaris-border p-2 sm:w-48 sm:border-b-0 sm:border-e"
            role="group"
            :aria-label="t('POLARIS_REPORTS.PERIOD.PRESETS')"
          >
            <Button
              v-for="preset in dateRanges.filter(
                item => item.value !== 'custom'
              )"
              :key="preset.value"
              variant="ghost"
              size="sm"
              class="justify-start font-normal"
              :class="
                preset.value === type ? 'bg-polaris-accent font-medium' : ''
              "
              :aria-pressed="preset.value === type"
              :data-preset="preset.value"
              @click="choosePreset(preset.value)"
            >
              {{ t(preset.label) }}
            </Button>
          </div>
          <div class="flex flex-col">
            <RangeCalendar
              v-model="picked"
              :number-of-months="twoMonths ? 2 : 1"
              :locale="intlLocale"
              :week-starts-on="WEEK_STARTS_ON"
            />
            <p
              class="px-4 pb-3 type-polaris-caption text-polaris-muted-foreground"
            >
              {{ t('POLARIS_REPORTS.PERIOD.HELP') }}
            </p>
            <div
              class="flex items-center justify-end gap-2 border-0 border-t border-solid border-polaris-border p-3"
            >
              <Button
                size="sm"
                :disabled="!picked.start || !picked.end"
                data-period-apply
                @click="apply"
              >
                {{ t('DATE_PICKER.APPLY_BUTTON') }}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
    <template v-if="navigable">
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            variant="ghost"
            size="icon-sm"
            :aria-label="t('DATE_PICKER.PREVIOUS_PERIOD')"
            @click="move(-1)"
          >
            <ChevronLeft16 class="rtl:rotate-180" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{{ t('DATE_PICKER.PREVIOUS_PERIOD') }}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            variant="ghost"
            size="icon-sm"
            :disabled="!canGoNext"
            :aria-label="t('DATE_PICKER.NEXT_PERIOD')"
            @click="move(1)"
          >
            <ChevronRight16 class="rtl:rotate-180" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{{ t('DATE_PICKER.NEXT_PERIOD') }}</TooltipContent>
      </Tooltip>
    </template>
  </div>
</template>
